import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, AlertCircle, Loader2, CreditCard } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending"); // pending, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch PayPal client ID
    axios.get(`${API}/paypal/client-id`)
      .then(res => {
        setPaypalClientId(res.data.client_id);
        setLoading(false);
      })
      .catch(err => {
        setError("Failed to load payment system");
        setLoading(false);
      });
  }, []);

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-invoice-payment/${data.orderID}`);
      setPaymentStatus("success");
      toast.success("Payment successful!");
    } catch (error) {
      setPaymentStatus("error");
      setError("Payment failed. Please try again or contact support.");
      toast.error("Payment failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#0A3F2F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
      <motion.div
        className="bg-white rounded-2xl max-w-md w-full p-8 shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-[#0A3F2F] flex items-center justify-center">
            <span className="text-white font-bold text-xl">S</span>
          </div>
          <span className="font-['Syne'] font-bold text-2xl text-[#0A3F2F]">Streamcaster</span>
        </div>

        {paymentStatus === "success" ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-3">Payment Successful!</h2>
            <p className="text-[#4A5F55] mb-6">Thank you for your payment. You will receive a confirmation email shortly.</p>
            <button
              onClick={() => navigate("/")}
              className="bg-[#0A3F2F] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0d5139] transition-colors"
            >
              Back to Website
            </button>
          </div>
        ) : paymentStatus === "error" || error ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-red-600" />
            </div>
            <h2 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-3">Payment Failed</h2>
            <p className="text-[#4A5F55] mb-6">{error || "Something went wrong. Please try again."}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#0A3F2F] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0d5139] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#D4FF33] rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard size={28} className="text-[#0A3F2F]" />
              </div>
              <h2 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-2">Complete Your Payment</h2>
              <p className="text-[#4A5F55] text-sm">Secure payment powered by PayPal</p>
            </div>

            {paypalClientId && (
              <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD" }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill" }}
                  createOrder={() => Promise.resolve(orderId)}
                  onApprove={onApprove}
                  onError={() => {
                    setPaymentStatus("error");
                    setError("Payment error. Please try again.");
                  }}
                />
              </PayPalScriptProvider>
            )}

            <p className="text-xs text-center text-[#4A5F55] mt-6">
              By completing this payment, you agree to our terms of service.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
